/**
 * Everything a coach has a relationship with, in the piles they think in.
 *
 * The list is grouped rather than sorted, because "answer this", "confirm
 * this", "you are booked" and "you could offer" are four different jobs and a
 * single date-ordered list hides which is which.
 *
 * The gate is stated at the bottom of every tab. A coach who does not know that
 * their list is filtered by qualification reads a short list as a broken app,
 * and the whole product rests on that rule.
 */

import { useState } from 'react'
import { useAuth } from '../../auth/AuthContext'
import { getTrainingProgress, listCoachEvents, listReportsDue } from '../../lib/api'
import { useAsync } from '../../lib/useAsync'
import { CoachEventListItem } from '../../components/EventCard'
import {
  EmptyState,
  ErrorState,
  Loading,
  Notice,
  PageHeader,
  Section,
} from '../../components/ui'
import { coachSchedule, useLiveReload, useNow } from './coachHooks'
import { QualificationGate } from './coachUi'
import type { CoachEventCard } from '../../lib/api'

type Tab = 'upcoming' | 'past' | 'cancelled'

export function CoachEvents() {
  const { profile } = useAuth()
  const coachId = profile?.id ?? ''
  const now = useNow(60_000)
  const [tab, setTab] = useState<Tab>('upcoming')

  const { data, error, loading, reload } = useAsync(async () => {
    const [cards, due, progress] = await Promise.all([
      listCoachEvents(coachId),
      listReportsDue(coachId),
      getTrainingProgress(coachId),
    ])
    return { cards, due, readiness: progress.readiness }
  }, [coachId])
  useLiveReload(reload)

  if (loading && !data) return <Loading label="Loading your events" />
  if (error && !data) return <ErrorState message={error} onRetry={reload} />
  if (!data) return null

  const schedule = coachSchedule(data.cards, now)
  const dueIds = new Set(data.due.map((d) => d.event.id))
  const reportsDue = schedule.past.filter((c) => dueIds.has(c.event.id))
  const worked = schedule.past.filter((c) => !dueIds.has(c.event.id))

  const tabs: Array<{ id: Tab; label: string; count: number }> = [
    { id: 'upcoming', label: 'Upcoming', count: schedule.upcoming.length },
    { id: 'past', label: 'Worked', count: schedule.past.length },
  ]
  if (schedule.cancelled.length > 0) {
    tabs.push({ id: 'cancelled', label: 'Cancelled', count: schedule.cancelled.length })
  }
  const active = tabs.some((t) => t.id === tab) ? tab : 'upcoming'

  const nothingUpcoming =
    schedule.inProgress.length === 0 &&
    schedule.needsAnswer.length === 0 &&
    schedule.needsConfirm.length === 0 &&
    schedule.booked.length === 0 &&
    schedule.waitingOnMichel.length === 0 &&
    schedule.openToYou.length === 0

  return (
    <>
      <PageHeader
        title="My events"
        subtitle="Everything you were asked about, assigned to, or have already worked."
      />

      {error ? (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <Notice tone="warning" title="Showing what was loaded earlier">
            {error}
          </Notice>
        </div>
      ) : null}

      <div className="filters" role="tablist" aria-label="Filter events">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={active === t.id}
            className={`chip${active === t.id ? ' is-active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            {t.count > 0 ? ` (${t.count})` : ''}
          </button>
        ))}
      </div>

      {active === 'upcoming' ? (
        nothingUpcoming ? (
          <EmptyState
            title="Nothing on your list"
            body="Availability requests and assignments land here. Only events for services you are qualified for ever will."
          />
        ) : (
          <>
            <Group
              title="Happening now"
              cards={schedule.inProgress}
              note="You are on this one right now. The brief and the materials list are inside."
            />
            <Group
              title="Waiting on your answer"
              cards={schedule.needsAnswer}
              note="Michel asked whether you are free. He cannot staff these until you answer."
            />
            <Group
              title="Confirm you are coming"
              cards={schedule.needsConfirm}
              note="You are assigned. Confirm so Michel knows you have seen it."
            />
            <Group
              title="You are booked"
              cards={schedule.booked}
              note="Confirmed. Reminders land 48 and 24 hours before each one."
            />
            <Group
              title="Waiting on Michel"
              cards={schedule.waitingOnMichel}
              note="You answered. He decides who goes and you will hear either way."
            />
            <Group
              title="Open to you"
              cards={schedule.openToYou}
              note="Nobody has asked you about these yet. You are qualified for them, so you can offer."
            />
          </>
        )
      ) : null}

      {active === 'past' ? (
        schedule.past.length === 0 ? (
          <EmptyState
            title="No events worked yet"
            body="Once you have worked an event it stays here, with the report you filed for it."
          />
        ) : (
          <>
            <Group
              title="Waiting on your report"
              cards={reportsDue}
              note="These are done, but the questionnaire is not. It takes two minutes."
            />
            <Group title="Worked" cards={worked} />
          </>
        )
      ) : null}

      {active === 'cancelled' ? (
        <Group
          title="Cancelled"
          cards={schedule.cancelled}
          note="Called off by the client or by Michel. Nothing is expected from you."
        />
      ) : null}

      <Section title="Your access">
        <QualificationGate readiness={data.readiness} />
      </Section>
    </>
  )
}

function Group({
  title,
  cards,
  note,
}: {
  title: string
  cards: CoachEventCard[]
  note?: string
}) {
  if (cards.length === 0) return null
  return (
    <Section title={title} count={cards.length}>
      {note ? (
        <p className="small muted" style={{ marginBottom: 'var(--sp-3)' }}>
          {note}
        </p>
      ) : null}
      <div className="stack">
        {cards.map((card) => (
          <CoachEventListItem key={card.event.id} card={card} to={`/events/${card.event.id}`} />
        ))}
      </div>
    </Section>
  )
}
